import {
  Design,
  Areas,
  Flow,
  Group,
  InterAreaConnection,
  JustInTimeRoutingConfig,
  LoadType,
  LogicalSpace,
  Obstacle,
  VehicleType,
  Floorplan,
  Track,
  TrafficRulesCatalog,
  MetaData,
  Templates,
  FlowStop,
  FlowLayer,
  FloorplanRevision,
  ReferenceImage,
  UxOnly,
  FileReference,
} from '@thive/fddm-types';

import { logger } from '@/modules/common/logging/logger';
import { Area, Template } from '@/modules/floorplanDesign/types';
import { FddmPreferenceValues } from '@/modules/floorplanDesign/preferences';

import { FddmItemUpdates, IFddmService, TemplateRegeneration } from './IFddmService';

export class FddmServiceExceptionDecorator implements IFddmService {
  private decoree: IFddmService;

  constructor(decoree: IFddmService) {
    this.decoree = decoree;
  }

  initialize(revsion: FloorplanRevision): void {
    try {
      this.decoree.initialize(revsion);
    } catch (err) {
      this.logError('initialize', err);
    }
  }

  async regenerateAll(): Promise<void> {
    try {
      await this.decoree.regenerateAll();
    } catch (err) {
      this.logError('regenerateAll', err);
    }
  }

  getModel(): FloorplanRevision {
    let model: FloorplanRevision;
    try {
      model = this.decoree.getModel();
    } catch (err) {
      this.logError('getModel', err);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return model!;
  }

  //
  // MetaData
  //

  getMetaData(): MetaData {
    let metaData: MetaData;
    try {
      metaData = this.decoree.getMetaData();
    } catch (err) {
      this.logError('getMetaData', err);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return metaData!;
  }

  setMetaData(metaData: MetaData): void {
    try {
      this.decoree.setMetaData(metaData);
    } catch (err) {
      this.logError('setMetaData', err);
    }
  }

  //
  // Reference image
  //

  getReferenceImage(): UxOnly['referenceImage'] {
    try {
      return this.decoree.getReferenceImage();
    } catch (err) {
      this.logError('getReferenceImage', err);
    }
  }

  addReferenceImage(referenceImage: ReferenceImage): void {
    try {
      this.decoree.addReferenceImage(referenceImage);
    } catch (err) {
      this.logError('addReferenceImage', err);
    }
  }

  updateReferenceImage(referenceImage: ReferenceImage): void {
    try {
      this.decoree.updateReferenceImage(referenceImage);
    } catch (err) {
      this.logError('updateReferenceImage', err);
    }
  }

  deleteReferenceImage(): void {
    try {
      this.decoree.deleteReferenceImage();
    } catch (err) {
      this.logError('deleteReferenceImage', err);
    }
  }

  //
  // Point cloud
  //

  addPointCloudInputFileRef(fileRef: string): void {
    try {
      this.decoree.addPointCloudInputFileRef(fileRef);
    } catch (err) {
      this.logError('addPointCloudInputFileRef', err);
    }
  }

  addPointCloudOutputFileRef(fileRef: string): void {
    try {
      this.decoree.addPointCloudOutputFileRef(fileRef);
    } catch (err) {
      this.logError('addPointCloudOutputFileRef', err);
    }
  }

  deletePointCloudInputFileRef(): void {
    try {
      this.decoree.deletePointCloudInputFileRef();
    } catch (err) {
      this.logError('deletePointCloudInputFileRef', err);
    }
  }

  deletePointCloudOutputFileRef(): void {
    try {
      this.decoree.deletePointCloudOutputFileRef();
    } catch (err) {
      this.logError('deletePointCloudOutputFileRef', err);
    }
  }

  //
  // Design
  //

  getDesign(): Design {
    let design: Design;
    try {
      design = this.decoree.getDesign();
    } catch (err) {
      this.logError('getDesign', err);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return design!;
  }

  setDesign(design: Design): void {
    try {
      this.decoree.setDesign(design);
    } catch (err) {
      this.logError('setDesign', err);
    }
  }

  //
  // Areas
  //

  getArea(id: string): Area | undefined {
    try {
      return this.decoree.getArea(id);
    } catch (err) {
      this.logError('getArea', err);
    }
  }

  async addAreas(areas: Areas): Promise<void> {
    try {
      await this.decoree.addAreas(areas);
    } catch (err) {
      this.logError('addAreas', err);
    }
  }

  updateArea(area: Area): void {
    try {
      this.decoree.updateArea(area);
    } catch (err) {
      this.logError('updateArea', err);
    }
  }

  updateAreas(areas: Areas): void {
    try {
      this.decoree.updateAreas(areas);
    } catch (err) {
      this.logError('updateAreas', err);
    }
  }

  deleteAreas(areas: Areas): void {
    try {
      this.decoree.deleteAreas(areas);
    } catch (err) {
      this.logError('deleteAreas', err);
    }
  }

  //
  // Obstacles
  //

  getObstacle(id: string): Obstacle | undefined {
    try {
      return this.decoree.getObstacle(id);
    } catch (err) {
      this.logError('getObstacle', err);
    }
  }

  async addObstacles(obstacles: Obstacle[]): Promise<void> {
    try {
      await this.decoree.addObstacles(obstacles);
    } catch (err) {
      this.logError('addObstacles', err);
    }
  }

  updateObstacle(obstacle: Obstacle): void {
    try {
      this.decoree.updateObstacle(obstacle);
    } catch (err) {
      this.logError('updateObstacle', err);
    }
  }

  updateObstacles(obstacles: Obstacle[]): void {
    try {
      this.decoree.updateObstacles(obstacles);
    } catch (err) {
      this.logError('updateObstacles', err);
    }
  }

  updateItems(items: FddmItemUpdates): void {
    try {
      this.decoree.updateItems(items);
    } catch (err) {
      this.logError('updateItems', err);
    }
  }

  deleteObstacles(obstacles: Obstacle[]): void {
    try {
      this.decoree.deleteObstacles(obstacles);
    } catch (err) {
      this.logError('deleteObstacles', err);
    }
  }

  //
  // Inter Area Connections
  //

  getInterAreaConnection(id: string): InterAreaConnection | undefined {
    try {
      return this.decoree.getInterAreaConnection(id);
    } catch (err) {
      this.logError('getInterAreaConnection', err);
    }
  }

  addInterAreaConnections(connections: InterAreaConnection[]): boolean {
    try {
      return this.decoree.addInterAreaConnections(connections);
    } catch (err) {
      this.logError('addInterAreaConnections', err);
      return false;
    }
  }

  updateInterAreaConnections(connections: InterAreaConnection[]): void {
    try {
      this.decoree.updateInterAreaConnections(connections);
    } catch (err) {
      this.logError('updateInterAreaConnections', err);
    }
  }

  deleteInterAreaConnections(connections: InterAreaConnection[]): void {
    try {
      this.decoree.deleteInterAreaConnections(connections);
    } catch (err) {
      this.logError('deleteInterAreaConnections', err);
    }
  }

  //
  // Logical Spaces
  //

  getLogicalSpace(id: string): LogicalSpace | undefined {
    try {
      return this.decoree.getLogicalSpace(id);
    } catch (err) {
      this.logError('getLogicalSpace', err);
    }
  }

  async addLogicalSpaces(logicalSpaces: LogicalSpace[]): Promise<void> {
    try {
      await this.decoree.addLogicalSpaces(logicalSpaces);
    } catch (err) {
      this.logError('addLogicalSpaces', err);
    }
  }

  updateLogicalSpace(logicalSpace: LogicalSpace): void {
    try {
      this.decoree.updateLogicalSpace(logicalSpace);
    } catch (err) {
      this.logError('updateLogicalSpace', err);
    }
  }

  deleteLogicalSpaces(logicalSpaces: LogicalSpace[]): void {
    try {
      this.decoree.deleteLogicalSpaces(logicalSpaces);
    } catch (err) {
      this.logError('deleteLogicalSpaces', err);
    }
  }

  //
  // Templates
  //

  getTemplate(id: string): Template | undefined {
    try {
      return this.decoree.getTemplate(id);
    } catch (err) {
      this.logError('getTemplate', err);
    }
  }

  addTemplates(templates: Templates): void {
    try {
      this.decoree.addTemplates(templates);
    } catch (err) {
      this.logError('addTemplates', err);
    }
  }

  async addTemplate(
    template: Template,
    areas: Areas,
    interAreaConnections: InterAreaConnection[],
    flowLayer: FlowLayer,
  ): Promise<void> {
    try {
      await this.decoree.addTemplate(template, areas, interAreaConnections, flowLayer);
    } catch (err) {
      this.logError('addTemplate', err);
    }
  }

  updateTemplate(template: Template): void {
    try {
      this.decoree.updateTemplate(template);
    } catch (err) {
      this.logError('updateTemplate', err);
    }
  }

  async regenerateTemplates(regenerations: TemplateRegeneration[]): Promise<void> {
    try {
      await this.decoree.regenerateTemplates(regenerations);
    } catch (err) {
      this.logError('regenerateTemplates', err);
    }
  }

  updateTemplates(templates: Templates): void {
    try {
      this.decoree.updateTemplates(templates);
    } catch (err) {
      this.logError('updateTemplates', err);
    }
  }

  deleteTemplates(templates: Templates): void {
    try {
      this.decoree.deleteTemplates(templates);
    } catch (err) {
      this.logError('deleteTemplates', err);
    }
  }

  //
  // Groups
  //

  getGroup(id: string): Group | undefined {
    try {
      return this.decoree.getGroup(id);
    } catch (err) {
      this.logError('getGroup', err);
    }
  }

  addGroups(groups: Group[]): void {
    try {
      this.decoree.addGroups(groups);
    } catch (err) {
      this.logError('addGroups', err);
    }
  }

  updateGroup(group: Group): void {
    try {
      this.decoree.updateGroup(group);
    } catch (err) {
      this.logError('updateGroup', err);
    }
  }

  deleteGroups(groups: Group[]): void {
    try {
      this.decoree.deleteGroups(groups);
    } catch (err) {
      this.logError('deleteGroups', err);
    }
  }

  ungroupGroups(groups: Group[]): void {
    try {
      this.decoree.ungroupGroups(groups);
    } catch (err) {
      this.logError('ungroupGroups', err);
    }
  }

  //
  // Jit Routing
  //

  getJitRoutingConfig(id: string): JustInTimeRoutingConfig | undefined {
    try {
      return this.decoree.getJitRoutingConfig(id);
    } catch (err) {
      this.logError('getJitRoutingConfig', err);
    }
  }

  addJitRoutingConfigs(jitRoutingPoints: JustInTimeRoutingConfig[]): void {
    try {
      this.decoree.addJitRoutingConfigs(jitRoutingPoints);
    } catch (err) {
      this.logError('addJitRoutingConfigs', err);
    }
  }

  updateJitRoutingConfig(jitRoutingPoint: JustInTimeRoutingConfig): void {
    try {
      this.decoree.updateJitRoutingConfig(jitRoutingPoint);
    } catch (err) {
      this.logError('updateJitRoutingConfig', err);
    }
  }

  deleteJitRoutingConfigs(jitRoutingConfigs: JustInTimeRoutingConfig[]): void {
    try {
      this.decoree.deleteJitRoutingConfigs(jitRoutingConfigs);
    } catch (err) {
      this.logError('deleteJitRoutingConfigs', err);
    }
  }

  //
  // Flows
  //

  getFlow(id: string): Flow | undefined {
    try {
      return this.decoree.getFlow(id);
    } catch (err) {
      this.logError('getFlow', err);
    }
  }

  getFlowsByAreaId(areaId: string): Flow[] | undefined {
    try {
      return this.decoree.getFlowsByAreaId(areaId);
    } catch (err) {
      this.logError('getFlowsByAreaId', err);
    }
  }

  addFlows(flows: Flow[]): void {
    try {
      this.decoree.addFlows(flows);
    } catch (err) {
      this.logError('addFlows', err);
    }
  }

  updateFlow(flow: Flow): void {
    try {
      this.decoree.updateFlow(flow);
    } catch (err) {
      this.logError('updateFlow', err);
    }
  }

  updateFlows(flows: Flow[]): void {
    try {
      this.decoree.updateFlows(flows);
    } catch (err) {
      this.logError('updateFlows', err);
    }
  }

  deleteFlows(flows: Flow[]): void {
    try {
      this.decoree.deleteFlows(flows);
    } catch (err) {
      this.logError('deleteFlows', err);
    }
  }

  //
  // FlowStops
  //

  getFlowStop(id: string): FlowStop | undefined {
    try {
      return this.decoree.getFlowStop(id);
    } catch (err) {
      this.logError('getFlowStop', err);
    }
  }

  getFlowStopByAreaId(areaId: string): FlowStop | undefined {
    try {
      return this.decoree.getFlowStopByAreaId(areaId);
    } catch (err) {
      this.logError('getFlowStopByAreaId', err);
    }
  }

  addFlowStops(flowStops: FlowStop[]): void {
    try {
      this.decoree.addFlowStops(flowStops);
    } catch (err) {
      this.logError('addFlowStops', err);
    }
  }

  updateFlowStop(flowStop: FlowStop): void {
    try {
      this.decoree.updateFlowStop(flowStop);
    } catch (err) {
      this.logError('updateFlowStop', err);
    }
  }

  deleteFlowStops(flowStops: FlowStop[]): void {
    try {
      this.decoree.deleteFlowStops(flowStops);
    } catch (err) {
      this.logError('deleteFlowStops', err);
    }
  }

  //
  // Vehicles
  //

  getVehicleType(id: string): VehicleType | undefined {
    try {
      return this.decoree.getVehicleType(id);
    } catch (err) {
      this.logError('getVehicleType', err);
    }
  }

  addVehicleTypes(vehicleTypes: VehicleType[]): void {
    try {
      this.decoree.addVehicleTypes(vehicleTypes);
    } catch (err) {
      this.logError('addVehicleTypes', err);
    }
  }

  addVehiclesToHighways(vehicleTypes: FloorplanRevision['setup']['vehicleTypes']): void {
    try {
      this.decoree.addVehiclesToHighways(vehicleTypes);
    } catch (err) {
      this.logError('addVehiclesToHighways', err);
    }
  }

  updateVehicleType(vehicleType: VehicleType): void {
    try {
      this.decoree.updateVehicleType(vehicleType);
    } catch (err) {
      this.logError('updateVehicleType', err);
    }
  }

  deleteVehicleTypes(vehicleTypes: VehicleType[]): void {
    try {
      this.decoree.deleteVehicleTypes(vehicleTypes);
    } catch (err) {
      this.logError('deleteVehicleTypes', err);
    }
  }

  //
  // Load Carriers
  //

  getLoadType(id: string): LoadType | undefined {
    try {
      return this.decoree.getLoadType(id);
    } catch (err) {
      this.logError('getLoadType', err);
    }
  }

  addLoadTypes(loadTypes: LoadType[]): void {
    try {
      this.decoree.addLoadTypes(loadTypes);
    } catch (err) {
      this.logError('addLoadTypes', err);
    }
  }

  deleteLoadTypes(loadTypes: LoadType[]): void {
    try {
      this.decoree.deleteLoadTypes(loadTypes);
    } catch (err) {
      this.logError('deleteLoadTypes', err);
    }
  }

  //
  // Catalog
  //

  setCatalogTrafficRules(trafficRules: TrafficRulesCatalog): void {
    try {
      this.decoree.setCatalogTrafficRules(trafficRules);
    } catch (err) {
      this.logError('setCatalogTrafficRules', err);
    }
  }

  getAllFileReferences(): FileReference[] {
    try {
      return this.decoree.getAllFileReferences();
    } catch (err) {
      this.logError('getAllFileReferences', err);
      throw err;
    }
  }

  getFileReferences(type: FileReference['type']): FileReference[] {
    try {
      return this.decoree.getFileReferences(type);
    } catch (err) {
      this.logError(`getFileReferences: ${type}`, err);
      throw err;
    }
  }

  addFileReference(fileReference: FileReference): void {
    try {
      this.decoree.addFileReference(fileReference);
    } catch (err) {
      this.logError('addFileReference', err);
      throw err;
    }
  }

  deleteFileReference(fileReference: FileReference): void {
    try {
      this.decoree.deleteFileReference(fileReference);
    } catch (err) {
      this.logError('deleteFileReference', err);
      throw err;
    }
  }

  //
  // Floorplan
  //

  getFloorplan(): Floorplan | undefined {
    try {
      return this.decoree.getFloorplan();
    } catch (err) {
      this.logError('getFloorplan', err);
    }
  }

  setFloorplan(floorplan: Floorplan): void {
    try {
      this.decoree.setFloorplan(floorplan);
    } catch (err) {
      this.logError('setFloorplan', err);
    }
  }

  //
  // Tracks
  //

  getTracks(): Track[] | undefined {
    try {
      return this.decoree.getTracks();
    } catch (err) {
      this.logError('getTracks', err);
    }
  }

  setTracks(tracks: Track[]): void {
    try {
      this.decoree.setTracks(tracks);
    } catch (err) {
      this.logError('setTracks', err);
    }
  }

  //
  // Selection
  //

  deleteSelection(selectedIds: string[]): void {
    try {
      this.decoree.deleteSelection(selectedIds);
    } catch (err) {
      this.logError('deleteSelection', err);
    }
  }

  mirrorSelectionHorizontally(selectedIds: string[]): void {
    try {
      this.decoree.mirrorSelectionHorizontally(selectedIds);
    } catch (err) {
      this.logError('mirrorSelectionHorizontally', err);
    }
  }

  mirrorSelectionVertically(selectedIds: string[]): void {
    try {
      this.decoree.mirrorSelectionVertically(selectedIds);
    } catch (err) {
      this.logError('mirrorSelectionVertically', err);
    }
  }

  rotateSelection90Degrees(selectedIds: string[]): void {
    try {
      this.decoree.rotateSelection90Degrees(selectedIds);
    } catch (err) {
      this.logError('rotateSelection90Degrees', err);
    }
  }

  changeName(id: string, newName: string): boolean {
    try {
      return this.decoree.changeName(id, newName);
    } catch (err) {
      this.logError('changeName', err);
      return false;
    }
  }

  //
  // Preferences
  //
  getPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
  ): FddmPreferenceValues[PreferenceKey] {
    try {
      return this.decoree.getPreference(key);
    } catch (err) {
      this.logError('getPreference', err);
      throw err;
    }
  }

  setPreference<PreferenceKey extends keyof FddmPreferenceValues>(
    key: PreferenceKey,
    value: FddmPreferenceValues[PreferenceKey],
  ): void {
    try {
      this.decoree.setPreference(key, value);
    } catch (err) {
      this.logError('setPreference', err);
    }
  }

  resetPreferences(): void {
    try {
      this.decoree.resetPreferences();
    } catch (err) {
      this.logError('resetPreferences', err);
    }
  }

  triggerAutosave(): void {
    try {
      this.decoree.triggerAutosave();
    } catch (err) {
      this.logError('triggerAutosave', err);
    }
  }

  disposeState(): void {
    try {
      this.decoree.disposeState();
    } catch (err) {
      this.logError('disposeState', err);
    }
  }

  // private utility methods

  private logError(methodName: string, error: unknown) {
    logger.error(`FddmService.${methodName} failed:`, error);
  }
}
